import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, router } from '@inertiajs/react'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '@/layouts/admin-layout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ArrowLeftIcon, GlobeIcon } from 'lucide-react'

interface Domain {
  id: number
  domain_name: string
  registration_type: 'purchased' | 'byod'
  purchase_status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  activation_status: 'not_activated' | 'configuring_dns' | 'activating_certificate' | 'active' | 'failed' | 'stale'
  cost_credits: number | null
  is_apex_domain: boolean
  cname_target: string | null
  expires_at: string | null
  activated_at: string | null
  created_at: string
  updated_at: string
  purchase_error: string | null
  activation_error: string | null
  registered_at: string | null
  dns_verified_at: string | null
  domain_readiness_checked_at: string | null
  route53_hosted_zone_id: string | null
  route53_operation_id: string | null
  registrar_domain_id: string | null
  acm_certificate_arn: string | null
  acm_validation_cname_name: string | null
  acm_validation_cname_value: string | null
  acm_validation_records: Array<Record<string, string>>
  ipv4_addresses: string[]
  ipv6_addresses: string[]
  sweep_started_at: string | null
  project: { id: number; name: string; archived: boolean }
  organization: { id: number; name: string } | null
}

interface ShowProps {
  domain: Domain
}

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleString(getDateLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className={`mt-1 text-sm text-foreground ${mono ? 'font-mono break-all' : ''}`}>
        {value || '-'}
      </p>
    </div>
  )
}

const ACTIVATION_BADGE_CONFIG: Record<Domain['activation_status'], { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  active: { variant: 'default', className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700' },
  not_activated: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700' },
  configuring_dns: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  activating_certificate: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  failed: { variant: 'destructive', className: '' },
  stale: { variant: 'destructive', className: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700' },
}

const PURCHASE_BADGE_CONFIG: Record<Domain['purchase_status'], { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  completed: { variant: 'default', className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700' },
  pending: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700' },
  processing: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  failed: { variant: 'destructive', className: '' },
  expired: { variant: 'secondary', className: '' },
}

function Show({ domain }: ShowProps) {
  const { t } = useTranslation('admin')
  const [showRelease, setShowRelease] = useState(false)

  const handleRelease = () => {
    router.delete(`/admin/domains/${domain.id}`, {
      onFinish: () => setShowRelease(false),
    })
  }

  const aBadge = ACTIVATION_BADGE_CONFIG[domain.activation_status]
  const pBadge = PURCHASE_BADGE_CONFIG[domain.purchase_status]

  const hasCertificate = domain.acm_certificate_arn ||
    domain.acm_validation_cname_name || domain.acm_validation_records.length > 0
  const hasDns = domain.cname_target || domain.ipv4_addresses.length > 0 ||
    domain.ipv6_addresses.length > 0 || domain.route53_hosted_zone_id

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
          <div className="px-4 lg:px-6">
            {/* Back link */}
            <Link
              href="/admin/domains"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t('domains.show.backToList')}
            </Link>

            {/* Header */}
            <div className="mb-6 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <GlobeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{domain.domain_name}</h1>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant={domain.registration_type === 'purchased' ? 'default' : 'secondary'}>
                      {t(`domains.badges.${domain.registration_type}`)}
                    </Badge>
                    <Badge variant={aBadge.variant} className={aBadge.className}>
                      {t(`domains.badges.${domain.activation_status}`)}
                    </Badge>
                    {domain.is_apex_domain && (
                      <Badge variant="outline">Apex</Badge>
                    )}
                    {domain.project.archived && (
                      <Badge variant="outline">{t('domains.archived')}</Badge>
                    )}
                  </div>
                </div>
              </div>
              <Button variant="destructive" onClick={() => setShowRelease(true)}>
                {t('domains.release.button')}
              </Button>
            </div>

            {/* Error banners */}
            {domain.purchase_error && (
              <div className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
                <p className="text-sm font-medium text-red-800 dark:text-red-400">{t('domains.show.purchaseError')}</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300 font-mono">{domain.purchase_error}</p>
              </div>
            )}
            {domain.activation_error && (
              <div className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
                <p className="text-sm font-medium text-red-800 dark:text-red-400">{t('domains.show.activationError')}</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300 font-mono">{domain.activation_error}</p>
              </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Domain Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('domains.show.domainInfo')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={t('domains.show.domainName')} value={domain.domain_name} />
                    <Field label={t('domains.show.registrationType')} value={
                      <Badge variant={domain.registration_type === 'purchased' ? 'default' : 'secondary'}>
                        {t(`domains.badges.${domain.registration_type}`)}
                      </Badge>
                    } />
                    <Field label={t('domains.show.isApex')} value={domain.is_apex_domain ? 'Yes' : 'No'} />
                    <Field label={t('domains.show.project')} value={
                      <Link href={`/admin/projects/${domain.project.id}`} className="text-primary hover:underline">
                        {domain.project.name}
                      </Link>
                    } />
                    {domain.organization && (
                      <Field label={t('domains.show.organization')} value={
                        <Link href={`/admin/organizations/${domain.organization.id}`} className="text-primary hover:underline">
                          {domain.organization.name}
                        </Link>
                      } />
                    )}
                    <Field label={t('domains.show.createdAt')} value={formatDate(domain.created_at)} />
                    {domain.expires_at && (
                      <Field label={t('domains.show.expiresAt')} value={formatDate(domain.expires_at)} />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Purchase & Activation */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('domains.show.status')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={t('domains.show.purchaseStatus')} value={
                      <Badge variant={pBadge.variant} className={pBadge.className}>
                        {t(`domains.badges.${domain.purchase_status}`)}
                      </Badge>
                    } />
                    <Field label={t('domains.show.activationStatus')} value={
                      <Badge variant={aBadge.variant} className={aBadge.className}>
                        {t(`domains.badges.${domain.activation_status}`)}
                      </Badge>
                    } />
                    {domain.cost_credits != null && domain.cost_credits > 0 && (
                      <Field label={t('domains.show.costCredits')} value={`${domain.cost_credits} credits`} />
                    )}
                    <Field label={t('domains.show.registeredAt')} value={formatDate(domain.registered_at)} />
                    <Field label={t('domains.show.activatedAt')} value={formatDate(domain.activated_at)} />
                    <Field label={t('domains.show.dnsVerifiedAt')} value={formatDate(domain.dns_verified_at)} />
                  </div>
                </CardContent>
              </Card>

              {/* DNS Configuration */}
              {hasDns && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('domains.show.dnsConfig')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4">
                      {domain.cname_target && (
                        <Field label={t('domains.show.cnameTarget')} value={domain.cname_target} mono />
                      )}
                      {domain.ipv4_addresses.length > 0 && (
                        <Field label={t('domains.show.ipv4')} value={domain.ipv4_addresses.join(', ')} mono />
                      )}
                      {domain.ipv6_addresses.length > 0 && (
                        <Field label={t('domains.show.ipv6')} value={domain.ipv6_addresses.join(', ')} mono />
                      )}
                      {domain.route53_hosted_zone_id && (
                        <Field label={t('domains.show.hostedZoneId')} value={domain.route53_hosted_zone_id} mono />
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Certificate Details */}
              {hasCertificate && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('domains.show.certificate')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4">
                      {domain.acm_certificate_arn && (
                        <Field label={t('domains.show.acmArn')} value={domain.acm_certificate_arn} mono />
                      )}
                      {domain.acm_validation_cname_name && (
                        <Field label={t('domains.show.validationName')} value={domain.acm_validation_cname_name} mono />
                      )}
                      {domain.acm_validation_cname_value && (
                        <Field label={t('domains.show.validationValue')} value={domain.acm_validation_cname_value} mono />
                      )}
                      {domain.acm_validation_records.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('domains.show.validationRecords')}</p>
                          <div className="mt-1 space-y-2">
                            {domain.acm_validation_records.map((record, i) => (
                              <div key={i} className="rounded border border-border bg-muted/50 p-2 text-xs font-mono break-all">
                                {Object.entries(record).map(([k, v]) => (
                                  <div key={k}><span className="text-muted-foreground">{k}:</span> {v}</div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Internal / Debug */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">{t('domains.show.internal')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <Field label="ID" value={domain.id} />
                    <Field label={t('domains.show.sweepStartedAt')} value={formatDate(domain.sweep_started_at)} />
                    <Field label={t('domains.show.readinessCheckedAt')} value={formatDate(domain.domain_readiness_checked_at)} />
                    {domain.registrar_domain_id && (
                      <Field label={t('domains.show.registrarDomainId')} value={domain.registrar_domain_id} mono />
                    )}
                    {domain.route53_operation_id && (
                      <Field label={t('domains.show.route53OpId')} value={domain.route53_operation_id} mono />
                    )}
                    <Field label={t('domains.show.updatedAt')} value={formatDate(domain.updated_at)} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Release Dialog */}
      <AlertDialog open={showRelease} onOpenChange={setShowRelease}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('domains.release.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('domains.release.description', {
                domain: domain.domain_name,
                project: domain.project.name,
              })}
              {domain.registration_type === 'purchased' && (
                <span className="block mt-2 font-semibold text-destructive">
                  {t('domains.release.purchasedWarning')}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('domains.release.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleRelease}
            >
              {t('domains.release.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
